import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";

const DEFAULT_LEVEL_REF = { current: 0 };

const EMOTION_TO_EXPRESSION = {
  happy: "joy",
  joy: "joy",
  excited: "joy",
  confident: "joy",
  calm: "joy",
  sad: "sorrow",
  anxious: "sorrow",
  stressed: "sorrow",
  fear: "sorrow",
  afraid: "sorrow",
  angry: "angry",
  disgust: "angry",
};

function normalizeTargetName(name) {
  return name.toLowerCase().split(".").pop();
}

function collectMorphTargets(scene) {
  const targets = [];
  scene.traverse((node) => {
    if (!node.morphTargetDictionary || !node.morphTargetInfluences) return;
    Object.entries(node.morphTargetDictionary).forEach(([name, index]) => {
      targets.push({
        influences: node.morphTargetInfluences,
        index,
        name: normalizeTargetName(name),
      });
    });
  });
  return targets;
}

function resolveMorphKey(name, morphMap) {
  return Object.entries(morphMap)
    .find(([, aliases]) => aliases.includes(name))?.[0] || null;
}

export default function AvatarCharacter({
  character,
  status = "idle",
  emotion = null,
  expression = null,
  speaking = false,
  levelRef = DEFAULT_LEVEL_REF,
  fallbackLipSync = false,
  onTargets,
  framing,
}) {
  const { scene } = useGLTF(character.assetUrl);
  const group = useRef();
  const weights = useRef({});
  const phaseOffset = useMemo(() => Math.random() * Math.PI * 2, []);
  const morphTargets = useMemo(() => collectMorphTargets(scene), [scene]);
  const resolvedTargets = useMemo(
    () => morphTargets.map((target) => ({
      ...target,
      key: resolveMorphKey(target.name, character.morphs),
    })),
    [character.morphs, morphTargets],
  );
  const activeEmotion = expression
    || EMOTION_TO_EXPRESSION[(emotion || "").toLowerCase()]
    || null;
  const modelFraming = framing || character.voiceFraming || {};
  const basePosition = modelFraming.position || [0, 0, 0];
  const baseRotation = modelFraming.rotation || [0, Math.PI, 0];
  const baseScale = modelFraming.scale || 1;

  useEffect(() => {
    onTargets?.([...new Set(morphTargets.map(({ name }) => name))]);
  }, [morphTargets, onTargets]);

  useEffect(() => {
    Object.entries(character.pose || {}).forEach(([boneName, rotation]) => {
      scene.getObjectByName(boneName)?.rotation.set(...rotation);
    });
  }, [character.pose, scene]);

  useEffect(() => () => {
    resolvedTargets.forEach(({ influences, index, key }) => {
      if (key) influences[index] = 0;
    });
  }, [resolvedTargets]);

  useFrame(({ clock, pointer }, delta) => {
    const time = clock.getElapsedTime() + phaseOffset;
    const liveLevel = levelRef.current || 0;
    const fallbackLevel = speaking && fallbackLipSync
      ? 0.2 + Math.abs(Math.sin(time * 11.5)) * 0.34
      : 0;
    const mouthLevel = speaking ? Math.max(liveLevel, fallbackLevel) : 0;
    const lipSync = character.lipSync;
    const visemeIndex = Math.floor(time * lipSync.shapesPerSecond)
      % lipSync.sequence.length;
    const activeViseme = speaking ? lipSync.sequence[visemeIndex] : null;
    const naturalBlink = (time % 4.1) < 0.11;

    Object.keys(character.morphs).forEach((key) => {
      let target = 0;
      if (key === activeViseme) target = Math.min(1, 0.18 + mouthLevel * 1.65);
      if (key === activeEmotion) target = expression ? 1 : 0.52;
      if (key === "blink" && (expression === "blink" || naturalBlink)) target = 1;
      weights.current[key] = THREE.MathUtils.damp(
        weights.current[key] || 0,
        target,
        target > (weights.current[key] || 0) ? 20 : 11,
        delta,
      );
    });

    resolvedTargets.forEach(({ influences, index, key }) => {
      if (key) influences[index] = weights.current[key] || 0;
    });

    if (!group.current) return;
    const listening = status === "listening";
    const thinking = status === "thinking";
    group.current.position.y = basePosition[1] + Math.sin(time * 1.25) * 0.012;
    group.current.rotation.y = THREE.MathUtils.damp(
      group.current.rotation.y,
      baseRotation[1] + (thinking ? -0.09 : pointer.x * 0.045),
      4,
      delta,
    );
    group.current.rotation.x = THREE.MathUtils.damp(
      group.current.rotation.x,
      baseRotation[0] + (listening ? pointer.y * -0.025 : 0),
      4,
      delta,
    );
    group.current.rotation.z = THREE.MathUtils.damp(
      group.current.rotation.z,
      baseRotation[2] + (speaking ? Math.sin(time * 2.1) * 0.012 : 0),
      4,
      delta,
    );
  });

  return (
    <group
      ref={group}
      position={basePosition}
      rotation={baseRotation}
      scale={baseScale}
    >
      <primitive object={scene} />
    </group>
  );
}
